import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Users, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

export const EmailConfirmPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [userEmail, setUserEmail] = useState<string>('');

  useEffect(() => {
    const confirmEmail = async () => {
      try {
        console.log('=== EMAIL CONFIRMATION DEBUG ===');
        console.log('Current URL:', window.location.href);
        console.log('Search params:', Object.fromEntries(searchParams.entries()));
        console.log('Hash fragment:', window.location.hash);

        // Parse URL parameters and hash fragment
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        
        // Get tokens from hash (this is where Supabase puts them)
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const tokenType = hashParams.get('token_type');
        const expiresIn = hashParams.get('expires_in');
        const type = hashParams.get('type') || 'signup';
        
        console.log('Token details:', {
          accessToken: accessToken ? 'present' : 'missing',
          refreshToken: refreshToken ? 'present' : 'missing',
          tokenType,
          expiresIn,
          type
        });

        setDebugInfo({
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshToken,
          type: type,
          tokenType: tokenType,
          expiresIn: expiresIn,
          urlParams: Object.fromEntries(urlParams.entries()),
          hashParams: Object.fromEntries(hashParams.entries()),
          fullUrl: window.location.href
        });

        if (!accessToken) {
          throw new Error('No access token found in URL. Please use the link from your email.');
        }

        // Method 1: Try to set the session directly using the tokens
        if (accessToken && refreshToken) {
          console.log('Setting session with tokens...');
          
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) {
            console.error('setSession error:', error);
            throw error;
          }

          if (data.user) {
            console.log('Session set successfully:', data.user);
            setUserEmail(data.user.email || '');
            setStatus('success');
            setMessage('Email confirmed successfully! Welcome to Advisory AI.');
            
            // Wait a bit then redirect
            setTimeout(() => {
              navigate('/onboarding');
            }, 3000);
            return;
          }
        }

        // Method 2: Try exchange code for session (fallback)
        const code = urlParams.get('code');
        if (code) {
          console.log('Exchanging code for session...');
          
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          
          if (error) {
            console.error('exchangeCodeForSession error:', error);
            throw error;
          }

          if (data.user) {
            console.log('Code exchange successful:', data.user);
            setUserEmail(data.user.email || '');
            setStatus('success');
            setMessage('Email confirmed successfully! Welcome to Advisory AI.');
            
            setTimeout(() => {
              navigate('/onboarding');
            }, 3000);
            return;
          }
        }

        // If we get here, something went wrong
        throw new Error('Unable to confirm email. Please try the link again or request a new one.');

      } catch (error: any) {
        console.error('Email confirmation error:', error);
        setStatus('error');
        
        let errorMessage = 'Email confirmation failed. ';
        
        if (error.message?.includes('expired') || error.message?.includes('invalid_token')) {
          errorMessage += 'The confirmation link has expired. Please request a new one.';
        } else if (error.message?.includes('invalid')) {
          errorMessage += 'The confirmation link is invalid. Please try signing up again.';
        } else if (error.message?.includes('already_confirmed')) {
          errorMessage += 'This email has already been confirmed. Try signing in directly.';
          // Auto-redirect to home for already confirmed users
          setTimeout(() => {
            navigate('/');
          }, 3000);
        } else {
          errorMessage += error.message || 'Unknown error occurred.';
        }
        
        setMessage(errorMessage);
      }
    };

    confirmEmail();
  }, [searchParams, navigate]);

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleResendEmail = async () => {
    try {
      setMessage('Sending verification email...');
      
      // Try to get email from user input or URL
      let email = userEmail;
      if (!email) {
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        email = urlParams.get('email') || hashParams.get('email') || '';
      }
      
      if (!email) {
        const userInput = prompt('Please enter your email address:');
        if (!userInput) return;
        email = userInput;
      }
      
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });
      
      if (error) {
        throw error;
      }
      
      setMessage(`Verification email sent to ${email}! Please check your inbox and spam folder.`);
    } catch (error: any) {
      console.error('Resend error:', error);
      setMessage('Failed to resend email: ' + (error.message || 'Unknown error'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-orange-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-md bg-black/60 backdrop-blur-sm rounded-3xl p-8 border border-white/20 text-center"
      >
        <div className="flex items-center gap-3 justify-center mb-6">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-400 to-purple-500 flex items-center justify-center">
            <Users className="w-5 h-5 text-white" />
          </div>
          <span className="text-white font-bold text-xl">Advisory AI</span>
        </div>

        {status === 'loading' && (
          <div>
            <Loader2 className="w-12 h-12 text-orange-400 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">Verifying Your Email</h2>
            <p className="text-white/70 mb-6">Please wait while we confirm your email address...</p>
          </div>
        )}

        {status === 'success' && (
          <div>
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">Email Verified!</h2>
            <p className="text-white/70 mb-6">{message}</p>
            <div className="flex items-center justify-center gap-2 text-white/60">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Redirecting to onboarding...</span>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div>
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">Verification Failed</h2>
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6">
              <p className="text-red-200 text-sm">{message}</p>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={handleResendEmail}
                className="w-full px-6 py-3 rounded-2xl bg-gradient-to-r from-orange-500 to-purple-500 text-white font-semibold hover:from-orange-400 hover:to-purple-400 transition-all duration-300"
              >
                Resend Verification Email
              </button>
              
              <button
                onClick={handleBackToHome}
                className="w-full px-6 py-3 rounded-2xl bg-white/10 text-white font-semibold hover:bg-white/20 transition-all duration-300"
              >
                Back to Home
              </button>
            </div>
          </div>
        )}
        
        {/* Debug info for troubleshooting */}
        {debugInfo && (
          <details className="text-left bg-white/10 rounded-lg p-4 mt-4">
            <summary className="text-white/80 cursor-pointer mb-2">Debug Info</summary>
            <pre className="text-xs text-white/60 overflow-auto">
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </details>
        )}
      </motion.div>
    </div>
  );
};